<template>
  <v-card tile flat style="height:100%;">
    <v-card-text>
      <div style="text-align:left; margin:0.5rem;margin-bottom:1.5rem">
        <div class="headline">Root Certificate Authorities</div>
        <div class="body-1">
          Manage root CAs for signing device certificates. Create your own CA
          hierarchy or use AWS IoT registered CAs.
        </div>
      </div>
      <v-layout row wrap>
        <v-flex xs12 sm12 md12>
          <itemlist
            :headers="[
              {text: 'CA Name', value: 'ca_name'},
              {text: 'Key Algorithm', value: 'key_algorithm'},
              {text: 'Status', value: 'status'},
              {text: 'Registered in AWS', value: 'aws_registered'},
              {text: 'Created', value: 'created_at'},
            ]"
            :values="rootCAs"
            v-on:selected="rootCA = $event"
            v-on:unselected="rootCA = null"
          ></itemlist>
        </v-flex>
        <v-flex xs12 sm12 md12 v-if="rootCA">
          <v-card class="elevation-0">
            <v-card-title class="title" style="padding-top:1rem">
              <span
                >Details of <v-chip>{{ rootCA.ca_name }}</v-chip> Root CA</span
              >
            </v-card-title>
            <v-card-text style="padding-top:0;padding-bottom:0">
              <v-layout row wrap style="text-align: left;margin-left:2rem">
                <v-flex xs12>
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px">vpn_key</v-icon>CA
                      Name:</v-flex
                    >
                    <v-flex xs9>{{ rootCA.ca_name }}</v-flex>
                  </v-layout>
                </v-flex>

                <v-flex xs12>
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px">lock</v-icon
                      >Algorithm:</v-flex
                    >
                    <v-flex xs9
                      >{{ rootCA.key_algorithm
                      }}{{
                        rootCA.key_size ? ` (${rootCA.key_size}-bit)` : ""
                      }}</v-flex
                    >
                  </v-layout>
                </v-flex>

                <v-flex xs12>
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px">flag</v-icon
                      >Status:</v-flex
                    >
                    <v-flex xs9>
                      <v-chip
                        :color="getStatusColor(rootCA.status)"
                        text-color="white"
                        small
                      >
                        {{ rootCA.status }}
                      </v-chip>
                    </v-flex>
                  </v-layout>
                </v-flex>

                <v-flex xs12>
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px">cloud</v-icon>AWS
                      Registered:</v-flex
                    >
                    <v-flex xs9>
                      <v-chip
                        :color="rootCA.aws_registered ? 'green' : 'grey'"
                        text-color="white"
                        small
                      >
                        {{ rootCA.aws_registered ? "Yes" : "No" }}
                      </v-chip>
                    </v-flex>
                  </v-layout>
                </v-flex>

                <v-flex xs12>
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px">date_range</v-icon
                      >Created:</v-flex
                    >
                    <v-flex xs9>{{ formatDate(rootCA.created_at) }}</v-flex>
                  </v-layout>
                </v-flex>

                <v-flex xs12 v-if="rootCA.expires_at">
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px">event</v-icon
                      >Expires:</v-flex
                    >
                    <v-flex xs9>{{ formatDate(rootCA.expires_at) }}</v-flex>
                  </v-layout>
                </v-flex>

                <v-flex xs12 v-if="rootCA.has_private_key">
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px"
                        >lock_outline</v-icon
                      >Private Key:</v-flex
                    >
                    <v-flex xs9
                      ><v-chip color="green" text-color="white" small
                        >Available</v-chip
                      ></v-flex
                    >
                  </v-layout>
                </v-flex>

                <v-flex xs12 v-if="rootCA.has_certificate">
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px"
                        >verified_user</v-icon
                      >CA Cert:</v-flex
                    >
                    <v-flex xs9
                      ><v-chip color="blue" text-color="white" small
                        >Available</v-chip
                      ></v-flex
                    >
                  </v-layout>
                </v-flex>

                <v-flex xs12 v-if="rootCA.aws_ca_arn">
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px"
                        >cloud_queue</v-icon
                      >AWS CA ARN:</v-flex
                    >
                    <v-flex xs9 style="word-break: break-all;">{{
                      rootCA.aws_ca_arn
                    }}</v-flex>
                  </v-layout>
                </v-flex>

                <v-flex xs12 v-if="rootCA.certificate_path">
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px">folder</v-icon
                      >Cert Path:</v-flex
                    >
                    <v-flex xs9 style="word-break: break-all;">{{
                      rootCA.certificate_path
                    }}</v-flex>
                  </v-layout>
                </v-flex>

                <v-flex xs12 v-if="rootCA.notes">
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px">notes</v-icon
                      >Notes:</v-flex
                    >
                    <v-flex xs9>{{ rootCA.notes }}</v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>
            </v-card-text>
            <v-card-actions style="padding-top:0;">
              <v-spacer></v-spacer>
              <v-btn
                outline
                small
                color="red"
                v-on:click="
                  deleteCA(rootCA);
                  rootCA = null;
                "
              >
                <v-icon small>close</v-icon> Delete
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-flex>
      </v-layout>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-alert
        :value="refreshCAsHint"
        type="warning"
        class="hint"
        transition="scale-transition"
      >
        Please, refresh Root CAs list
      </v-alert>
      <v-btn
        dark
        icon
        class="indigo"
        v-on:click="
          fetchRootCAs();
          refreshCAsHint = false;
        "
      >
        <v-icon>sync</v-icon>
      </v-btn>
      <v-btn
        dark
        icon
        class="indigo"
        v-on:click="openCARegistrationProcess = !openCARegistrationProcess"
      >
        <v-icon>add</v-icon>
      </v-btn>
      <register-root-ca
        v-if="openCARegistrationProcess == true"
        v-on:abort="openCARegistrationProcess = false"
        v-on:completed="
          openCARegistrationProcess = false;
          refreshCAsHint = true;
        "
      ></register-root-ca>
    </v-card-actions>
  </v-card>
</template>
<script>
import {mapState} from "vuex";

export default {
  components: {
    itemlist: require("../../ItemList").default,
    "register-root-ca": require("./RegisterRootCA").default,
  },

  props: {
    theme: String,
  },

  data() {
    return {
      refreshCAsHint: false,
      rootCA: null,
      openCARegistrationProcess: false,
    };
  },

  computed: {
    ...mapState({
      lastOperationMessage: "lastOperationMessage",
      lastOperationSuccess: "lastOperationSuccess",
      operationInProcess: "operationInProcess",
      operationInProcessMessage: "operationInProcessMessage",
      rootCAs: (state) => (state.rootCAs && state.rootCAs.rootCAs) || [],
      appMetadata: (state) => state.metadata.models.views,
    }),
  },

  methods: {
    async fetchRootCAs() {
      try {
        await this.$store.dispatch("rootCAs/fetchRootCAs");
      } catch (error) {
        console.error("Error fetching Root CAs:", error);
      }
    },

    async downloadCA(caId) {
      // Download no longer supported — CA files are managed by IoT Core
    },

    async registerCAWithAWS(ca) {
      // Registration is now done at creation time
    },

    deleteCA(ca) {
      if (confirm(`Delete Root CA "${ca.ca_name}"? This cannot be undone.`)) {
        try {
          this.$store.dispatch("rootCAs/deleteCA", ca.ca_id);
          this.refreshCAsHint = true;
        } catch (error) {
          console.error("Error deleting CA:", error);
        }
      }
    },

    getStatusColor(status) {
      const colors = {
        active: "green",
        inactive: "grey",
        revoked: "red",
      };
      return colors[status] || "blue";
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date =
        typeof dateString === "object" ? dateString : new Date(dateString);
      return date.toLocaleString();
    },
  },

  mounted() {
    this.fetchRootCAs();
  },
};
</script>

<style scoped>
.hint {
  margin-right: 1rem;
  padding: -0.5rem;
}
</style>
